import { useMemo } from 'react'
import {
  Cog6ToothIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'
import { v4 as uuidv4 } from 'uuid'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/utils'
import { useDocumentState } from '@/state'
import {
  ReaderIcon,
  TableIcon,
  ImageIcon,
  TextIcon,
  VideoIcon,
} from '@radix-ui/react-icons'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { UploadPhotoButton } from '../UploadPhotoButton'
import { UploadVideoButton } from '../UploadVideoButton'
import { useCADQuery } from '@/services/queries/cads'
import { DocumentTypeChoices } from '@/lib/api/client'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'
import { useStepBOM } from '../OperationDocument/hooks/useBOM'
import {
  OperationNotesPlaceholder,
  OperationLabelPlaceholder,
} from './DraggablePlaceholders'
import {
  TableIdCellPlaceholder,
  TablePlaceholder,
} from './DraggablePlaceholders/TablePlaceholder'

type InsertElementMenuProps = {
  isDisabled: boolean
  documentType?: DocumentTypeChoices
}
export const InsertElementMenu = ({
  isDisabled,
  documentType,
}: InsertElementMenuProps) => {
  const selectedPageId = useDocumentState((state) => state.currentPageInView)
  const startAddingElement = useDocumentState(
    (state) => state.startAddingElement,
  )

  const { data: cadData } = useCADQuery()
  const documentPage = cadData?.documentPages.find(
    (page) => page.id === selectedPageId,
  )
  const { isLoading: readOnlyLoading, data: readOnlyData } = useIsReadOnly()

  const partsInOperation = useStepBOM({
    documentPageId: documentPage?.id,
  })

  const disableAddImage =
    !documentPage || readOnlyLoading || readOnlyData.isReadOnly

  const menuOptions = useMemo(
    () => [
      {
        title: 'Textbox',
        description: 'Space for notes',
        Icon: ReaderIcon,
        handler: (e) => {
          if (!documentPage) return

          startAddingElement({
            newElementData: {
              Component: OperationNotesPlaceholder,
              dataType: 'note',
              attributes: {
                content: '',
                notesPosition: 'absolute',
              },
              documentPageId: documentPage?.id as string,
            },
            triggerLocation: {
              x: e.clientX,
              y: e.clientY,
            },
          })
        },
        tooltip: 'Add Textbox',
      },
      {
        title: 'Label',
        description: 'Limited text space',
        Icon: TextIcon,
        handler: (e) => {
          if (!documentPage) return

          startAddingElement({
            newElementData: {
              Component: OperationLabelPlaceholder,
              dataType: 'label',
              attributes: {
                content: '',
                notesPosition: 'absolute',
              },
              documentPageId: documentPage?.id as string,
            },
            triggerLocation: {
              x: e.clientX,
              y: e.clientY,
            },
          })
        },
        tooltip: 'Add Textbox',
      },
      {
        title: 'CAD or Image',
        description: 'Add an image',
        Icon: ImageIcon,
        disabled: disableAddImage,
        Trigger: UploadPhotoButton,
        tooltip: 'Upload Image',
      },
      {
        title: 'Video',
        description: 'Upload a video',
        Icon: VideoIcon,
        disabled: disableAddImage,
        Trigger: UploadVideoButton,
        tooltip: 'Upload Video',
      },
      {
        title: 'Table',
        description: 'Generic tabular data',
        Icon: TableIcon,
        handler: async (e) => {
          const firstAccessor = `custom-column--${uuidv4()}`
          const secondAccessor = `custom-column--${uuidv4()}`
          const tableData = Array.from(Array(2)).map(() => ({
            [firstAccessor]: '',
            [secondAccessor]: '',
          }))

          const tableColumns = [
            {
              [firstAccessor]: '',
            },
            {
              [secondAccessor]: '',
            },
          ]

          startAddingElement({
            newElementData: {
              Component: (props: any) => (
                <TablePlaceholder
                  rows={tableData}
                  columns={{
                    [firstAccessor]: {
                      title: '',
                      width: 100,
                    },
                    [secondAccessor]: {
                      title: '',
                      width: 100,
                    },
                  }}
                  {...props}
                />
              ),
              dataType: 'table',
              attributes: {
                columns: tableColumns,
                rows: tableData,
                type: 'generic-table',
              },
              documentPageId: documentPage?.id as string,
            },
            triggerLocation: {
              x: e.clientX,
              y: e.clientY,
            },
          })

          /* await createTable({
            documentPageId: documentPage?.id as string,
            tableData: {
              columns: tableColumns,
              rows: tableData,
              type: 'generic-table',
              position: {
                x: 0,
                y: 190,
              },
            },
          }) */
        },
        tooltip: 'Insert Table',
      },
      {
        title: 'Parts Table',
        description: 'Additional parts table',
        Icon: Cog6ToothIcon,
        handler: async (e) => {
          const rows = partsInOperation.length
            ? partsInOperation.map((part, i) => {
                return {
                  id: (
                    <TableIdCellPlaceholder>
                      {String.fromCharCode(65 + i)}
                    </TableIdCellPlaceholder>
                  ),
                  partName: part.partName,
                  quantity: part.quantity,
                }
              })
            : [
                {
                  id: (
                    <TableIdCellPlaceholder>
                      {String.fromCharCode(65)}
                    </TableIdCellPlaceholder>
                  ),
                  partName: '',
                  quantity: '',
                },
              ]
          const columns = {
            id: {
              title: 'Id',
              width: 35,
            },
            partName: {
              title: 'Part Name',
              width: 340,
            },
            quantity: {
              title: 'Qty',
              width: 60,
            },
          }
          const initialRows = partsInOperation.length
            ? []
            : [{ partName: '', quantity: '' }]
          startAddingElement({
            newElementData: {
              Component: (props: any) => (
                <TablePlaceholder rows={rows} columns={columns} {...props} />
              ),
              dataType: 'table',
              attributes: {
                columns: [],
                rows: initialRows,
                type: 'parts-table',
                isManual: true,
              },
              documentPageId: documentPage?.id as string,
            },
            triggerLocation: {
              x: e.clientX,
              y: e.clientY,
            },
          })
        },
        tooltip: 'Insert Parts Table',
      },
      {
        title: 'Tools Table',
        description: 'Additional tools table',
        Icon: WrenchScrewdriverIcon,
        handler: async (e) => {
          startAddingElement({
            newElementData: {
              Component: (props: any) => (
                <TablePlaceholder
                  rows={[
                    {
                      toolName: '',
                    },
                  ]}
                  columns={{
                    toolName: {
                      title: 'Tools',
                      width: 200,
                    },
                  }}
                  {...props}
                />
              ),
              dataType: 'table',
              attributes: {
                columns: [],
                rows: [],
                type: 'tools-table',
                isManual: true,
              },
              documentPageId: documentPage?.id as string,
            },
            triggerLocation: {
              x: e.clientX,
              y: e.clientY,
            },
          })
        },
        tooltip: 'Insert Tools Table',
      },
    ],
    [disableAddImage, documentPage, partsInOperation, startAddingElement],
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={isDisabled}
        className={cn(
          'rounded-md hover:bg-gray-100 m-0 flex items-center justify-center h-full',
          {
            'opacity-25': isDisabled,
          },
        )}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="rounded-md hover:bg-gray-100 m-0 h-auto w-auto flex p-1 items-center justify-center min-w-[50px]"
                disabled={isDisabled}
              >
                <span className="flex items-center flex-col space-y-0.5">
                  <PlusIcon className="h-6 w-full stroke-2" />
                  <span className="text-xs">Add</span>
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              <p>Add element to document</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="flex min-w-0 px-1.5 py-2">
        <ul className="w-full">
          {menuOptions.map(
            (
              { title, description, Icon, handler, tooltip, Trigger, disabled },
              i,
            ) => (
              <li
                key={`option-${i}`}
                className="flex flex-row items-center justify-between mb-2 last:mb-0 border-b last:border-b-0 order-gray-100"
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {Trigger && documentPage ? (
                        <Trigger
                          documentPage={documentPage}
                          isDisabled={disabled}
                          documentType={documentType}
                        >
                          <Icon className="h-7 w-7 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium">{title}</p>
                            <p className="text-xs text-gray-500">
                              {description}
                            </p>
                          </div>
                        </Trigger>
                      ) : (
                        <DropdownMenuItem asChild disabled={disabled}>
                          <Button
                            variant="outline"
                            className="flex flex-row items-center space-x-2 hover:bg-gray-100 border-0 shadow-none w-full justify-start text-left h-full p-2"
                            onClick={handler}
                            disabled={disabled}
                          >
                            <Icon className="h-7 w-7 text-blue-500" />
                            <div>
                              <p className="text-sm font-medium">{title}</p>
                              <p className="text-xs text-gray-500">
                                {description}
                              </p>
                            </div>
                          </Button>
                        </DropdownMenuItem>
                      )}
                    </TooltipTrigger>
                    <TooltipContent className="bg-black text-white">
                      <p>{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </li>
            ),
          )}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
